import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Hand, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Card chrome shared by every inline HITL approval surface
 * (``ApprovalRequestBlock``, ``HarnessApprovalCard``, and the awaiting
 * branch of ``ComputerUseTaskBlock``).
 *
 *   * Rounded card, ``border-border bg-surface``, fade-in animation.
 *   * Icon tile (``Hand`` by default) + primary ``title`` +
 *     caption ``subtitle``.
 *   * ``urgent`` adds a calm ``animate-pulse`` to the icon tile so a
 *     pending approval still reads as "waiting for you" in a long
 *     scrolling transcript without the loud warning-yellow treatment
 *     the new design replaced.
 *   * ``compact`` switches to a tighter tile + smaller text so the
 *     same primitive fits the floating overlay form factor.
 *
 * The body lives in ``children`` — context panels, optional textarea,
 * action row. Each consumer composes those itself.
 */
export interface InlineApprovalCardShellProps {
  title: ReactNode;
  /** Omitted when the card has no supporting sentence under the title. */
  subtitle?: ReactNode;
  /** Lucide icon component; defaults to ``Hand``. */
  Icon?: LucideIcon;
  /**
   * Decorative-by-default. Pass a label when the icon carries meaning
   * not already conveyed by the title/subtitle text (e.g. the overlay's
   * compact card where the title may scroll off-screen for screen
   * readers).
   */
  iconAriaLabel?: string;
  /** Add a calm pulse to the icon tile while the approval is pending. */
  urgent?: boolean;
  /** Tighter padding + smaller icon tile for the overlay form factor. */
  compact?: boolean;
  /** Attribute on the root for legacy test selectors / DOM observers. */
  'data-testid'?: string;
  /** Sits at the end of the title row. */
  headerAside?: ReactNode;
  /** Card body — panels, textarea, actions, etc. */
  children?: ReactNode;
}

export const InlineApprovalCardShell = ({
  title,
  subtitle,
  Icon = Hand,
  iconAriaLabel,
  urgent = false,
  compact = false,
  'data-testid': dataTestId,
  headerAside,
  children,
}: InlineApprovalCardShellProps) => {
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-4 rounded-xl border border-border bg-surface p-4',
        'duration-300 animate-in fade-in slide-in-from-bottom-1',
        compact && 'gap-3 rounded-lg p-3'
      )}
      data-testid={dataTestId}
    >
      <div
        className={cn(
          'flex',
          subtitle ? 'items-start' : 'items-center',
          compact ? 'gap-2.5' : 'gap-3'
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md bg-surface-accent',
            compact ? 'size-7' : 'size-9 rounded-lg',
            urgent && 'animate-pulse'
          )}
          aria-hidden={iconAriaLabel ? undefined : true}
          aria-label={iconAriaLabel}
          role={iconAriaLabel ? 'img' : undefined}
        >
          <Icon className={cn('text-text-primary', compact ? 'size-3.5' : 'size-4')} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <p
            className={cn(
              'break-words font-medium leading-snug text-text-primary [overflow-wrap:anywhere]',
              compact ? 'text-[13px]' : 'text-sm'
            )}
          >
            {title}
          </p>
          {subtitle ? (
            compact ? (
              <span className="text-[11px] leading-snug text-text-secondary">
                {subtitle}
              </span>
            ) : (
              <TypographyCaption className="text-text-secondary">
                {subtitle}
              </TypographyCaption>
            )
          ) : null}
        </div>
        {headerAside ? <div className="shrink-0">{headerAside}</div> : null}
      </div>
      {children}
    </div>
  );
};
